import { useEffect, useState, useCallback } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { useDensity } from '../hooks/useDensity';
import {
  LayoutDashboard,
  Receipt,
  Clock,
  AlertTriangle,
  FileWarning,
  Building2,
  CreditCard,
  Sun,
  Moon,
  Maximize2,
  Minimize2,
  LogOut,
  Download,
} from 'lucide-react';

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [theme, setTheme] = useTheme();
  const [density, setDensity] = useDensity();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const go = useCallback(
    (path: string) => {
      setOpen(false);
      navigate(path);
    },
    [navigate],
  );

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Command palette"
      className="fixed inset-0 z-50 flex items-start justify-center bg-background/80 p-4 pt-[15vh]"
    >
      <Command className="w-full max-w-lg rounded-lg border border-border bg-card shadow-lg">
        <Command.Input
          placeholder="Type a command or search…"
          className="w-full border-b border-border bg-transparent p-4 text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
        />
        <Command.List className="max-h-96 overflow-auto p-2">
          <Command.Empty className="p-4 text-center text-sm text-muted-foreground">
            No results.
          </Command.Empty>
          <Command.Group
            heading="Navigation"
            className="text-xs text-muted-foreground/70 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
          >
            <Command.Item
              onSelect={() => go('/')}
              className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm text-foreground aria-selected:bg-background"
            >
              <LayoutDashboard size={16} /> Home
            </Command.Item>
            <Command.Item
              onSelect={() => go('/transactions')}
              className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm text-foreground aria-selected:bg-background"
            >
              <Receipt size={16} /> Transactions
            </Command.Item>
            <Command.Item
              onSelect={() => go('/pending')}
              className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm text-foreground aria-selected:bg-background"
            >
              <Clock size={16} /> Pending
            </Command.Item>
            <Command.Item
              onSelect={() => go('/alerts')}
              className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm text-foreground aria-selected:bg-background"
            >
              <AlertTriangle size={16} /> Alerts
            </Command.Item>
            <Command.Item
              onSelect={() => go('/parse-failed')}
              className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm text-foreground aria-selected:bg-background"
            >
              <FileWarning size={16} /> Parse failed
            </Command.Item>
            <Command.Item
              onSelect={() => go('/admin/departments')}
              className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm text-foreground aria-selected:bg-background"
            >
              <Building2 size={16} /> Departments
            </Command.Item>
            <Command.Item
              onSelect={() => go('/admin/cards')}
              className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm text-foreground aria-selected:bg-background"
            >
              <CreditCard size={16} /> Cards
            </Command.Item>
          </Command.Group>
          <Command.Group heading="Actions">
            <Command.Item
              onSelect={() => {
                setOpen(false);
                setTheme(theme === 'dark' ? 'light' : 'dark');
              }}
              className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm text-foreground aria-selected:bg-background"
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />} Toggle theme
            </Command.Item>
            <Command.Item
              onSelect={() => {
                setOpen(false);
                setDensity(density === 'compact' ? 'comfortable' : 'compact');
              }}
              className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm text-foreground aria-selected:bg-background"
            >
              {density === 'compact' ? <Maximize2 size={16} /> : <Minimize2 size={16} />} Toggle
              density ({density})
            </Command.Item>
            <Command.Item
              onSelect={() => {
                setOpen(false);
                window.location.href = '/api/transactions/export.csv';
              }}
              className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm text-foreground aria-selected:bg-background"
            >
              <Download size={16} /> Download CSV (all)
            </Command.Item>
            <Command.Item
              onSelect={async () => {
                setOpen(false);
                await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
                window.location.href = '/login';
              }}
              className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm text-foreground aria-selected:bg-background"
            >
              <LogOut size={16} /> Sign out
            </Command.Item>
          </Command.Group>
        </Command.List>
      </Command>
    </Command.Dialog>
  );
}
